// SharePopup.tsx - Updated with developer list from API
import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import Button from "../ui/buttons";
import { EnhancedTokenManager } from "../../utils/tokenManager";
import { useProfileImage } from "../../contexts/ProfileImageContext";
import { API_URLS, BASE_URL } from "../../api/urls";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

// User interface
interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: "Owner" | "Viewer";
}

// Developer response interface
interface DeveloperResponse {
  success: boolean;
  card_id: number;
  developers: Array<{
    email: string;
    name: string | null;
    profile_image: string | null;
  }>;
  workspace_owner_name: string;
}

// Share popup props
interface ShareFeedbackPopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  totalFeedback?: number;
  users?: User[];
  onShare?: (emails: string[]) => void;
  onCancel?: () => void;
  cardId?: number;
}

const ShareFeedbackPopup: React.FC<ShareFeedbackPopupProps> = ({
  isOpen,
  onClose,
  title = "Share with developers",
  totalFeedback = 29,
  users = [],
  onShare,
  onCancel,
  cardId,
}) => {
  const [emailInput, setEmailInput] = useState("");
  const [emailTags, setEmailTags] = useState<string[]>([]);
  const [inputFocused, setInputFocused] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [previousDevelopers, setPreviousDevelopers] = useState<User[]>([]);
  const [isLoadingDevelopers, setIsLoadingDevelopers] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get profile image from context
  const { profileImage } = useProfileImage();

  // Helper function to capitalize first letter of each word
  const capitalizeWords = (str: string): string => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Helper function to get initials from name
  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to get full image URL
  const getFullImageUrl = (imagePath: string | null): string | null => {
    if (!imagePath) {
      console.log("❌ No image path provided");
      return null;
    }

    // If it's already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      console.log("✅ Full URL already:", imagePath);
      return imagePath;
    }

    // Remove leading slash if present and clean up backslashes
    const cleanPath = imagePath.replace(/\\/g, "/").replace(/^\/+/, "");

    // Construct full URL with base URL

    const fullUrl = `${BASE_URL}/${cleanPath}`;
    console.log("🔗 Constructed URL:", fullUrl, "from path:", imagePath);
    return fullUrl;
  };

  // Fetch previous developers when popup opens
  useEffect(() => {
    const fetchPreviousDevelopers = async () => {
      if (!isOpen || !cardId) {
        setPreviousDevelopers([]);
        return;
      }

      setIsLoadingDevelopers(true);

      try {
        const accessToken = EnhancedTokenManager.getAccessToken();
        if (!accessToken) {
          console.error("No access token available");
          setIsLoadingDevelopers(false);
          return;
        }

        console.log("🔍 Fetching developers for card:", cardId);

        const response = await fetch(
          API_URLS.GET_DEVELOPER_CARD_SENDERS_LIST(cardId),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data: DeveloperResponse = await response.json();

        console.log("✅ Developers response:", data);

        if (data.success && data.developers && data.developers.length > 0) {
          // Transform API response to User format
          const developers: User[] = data.developers.map((dev, index) => {
            const fullImageUrl = getFullImageUrl(dev.profile_image);
            console.log(`👤 Developer ${index}:`, {
              name: dev.name,
              email: dev.email,
              originalPath: dev.profile_image,
              fullImageUrl: fullImageUrl,
              willUseInitials: !fullImageUrl,
            });

            return {
              id: `dev-${index}`,
              name: dev.name || dev.email,
              email: dev.email,
              avatar: fullImageUrl || undefined, // Set to undefined instead of initials string
              role: "Viewer" as const,
            };
          });

          setPreviousDevelopers(developers);
          console.log("✅ Previous developers loaded:", developers.length);
        } else {
          setPreviousDevelopers([]);
        }
      } catch (error) {
        console.error("❌ Error fetching developers:", error);
        setPreviousDevelopers([]);
      } finally {
        setIsLoadingDevelopers(false);
      }
    };

    fetchPreviousDevelopers();
  }, [isOpen, cardId]);

  // Fetch current user profile on component mount
  useEffect(() => {
    const fetchUserProfile = () => {
      const userProfile = EnhancedTokenManager.getUserProfile();

      if (userProfile) {
        const fullName = userProfile.full_name || "User";
        const capitalizedName = capitalizeWords(fullName);

        setCurrentUser({
          id: userProfile.id.toString(),
          name: capitalizedName,
          email: userProfile.email,
          avatar: capitalizedName.charAt(0).toUpperCase(),
          role: "Owner",
        });
      }
    };

    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen]);

  // Check if Share button should be active
  const isShareActive = emailTags.length > 0;

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle background click to close popup
  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle email input change
  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value);
  };

  // Add email tag
  const addEmailTag = (email: string) => {
    const trimmedEmail = email.trim();
    if (
      trimmedEmail &&
      isValidEmail(trimmedEmail) &&
      !emailTags.includes(trimmedEmail)
    ) {
      setEmailTags([...emailTags, trimmedEmail]);
      setEmailInput("");
    }
  };

  // Remove email tag
  const removeEmailTag = (emailToRemove: string) => {
    setEmailTags(emailTags.filter((email) => email !== emailToRemove));
  };

  // Handle key press in input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const trimmedInput = emailInput.trim();

    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      if (trimmedInput) {
        addEmailTag(trimmedInput);
      }
    } else if (e.key === "Backspace" && !emailInput && emailTags.length > 0) {
      setEmailTags(emailTags.slice(0, -1));
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    setInputFocused(false);
    const trimmedInput = emailInput.trim();
    if (trimmedInput) {
      addEmailTag(trimmedInput);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setInputFocused(true);
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const emails = pastedText.split(/[,\s;]+/).filter((email) => email.trim());

    emails.forEach((email) => {
      addEmailTag(email);
    });
  };

  const handleShare = () => {
    if (!isShareActive) return;
    onShare?.(emailTags);
    setEmailTags([]);
    setEmailInput("");
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    setEmailTags([]);
    setEmailInput("");
    onClose();
  };

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4 sm:px-6 lg:px-8"
      onClick={handleBackgroundClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl 4xl:max-w-4xl mx-auto">
        {/* Header */}
        <header className="flex items-start justify-start mx-4 sm:mx-4 xl:mx-6 2xl:mx-6 4xl:mx-12 pt-3 sm:pt-3 xl:pt-4 2xl:pt-4 4xl:pt-6 pb-2 sm:pb-2 xl:pb-3 2xl:pb-3 4xl:pb-5  border-b border-gray-200">
          <h2
            className="text-[22px] sm:text-base xl:text-[22px] 2xl:text-[22px] 3xl:text-2xl 4xl:text-3xl font-medium  text-[#050505] text-left"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {title}
          </h2>
        </header>

        {/* Content */}
        <main className="mx-4 sm:mx-4 xl:mx-6 2xl:mx-6 4xl:mx-12 py-3 sm:py-3 xl:py-6 2xl:py-6 4xl:py-10 space-y-4 sm:space-y-6 xl:space-y-8 2xl:space-y-8 4xl:space-y-12 mb-12">
          {/* Gmail-style Email input with tags */}
          <section>
            <div
              className={`min-h-[36px] w-full px-3 sm:px-2 xl:px-2 2xl:px-2 4xl:px-3 py-2 sm:py-2 xl:py-2 2xl:py-2 4xl:py-3 border rounded-[6px] flex flex-wrap items-center gap-2 cursor-text transition-all ${
                inputFocused
                  ? "border-[#49AFFF] "
                  : "border-[#66727F]"
              }`}
              onClick={() => inputRef.current?.focus()}
            >
              {/* Email Tags */}
              {emailTags.map((email, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 bg-[#E5F4FF] border border-[#A3D8FF] text-[#192028] font-light rounded-[4px] px-2 sm:px-[8px] py-1  font-inter text-xs 3xl:text-sm"
               
                >
                  <span>{email}</span>
                  <button
                    onClick={(e) => {  
                      e.stopPropagation();
                      removeEmailTag(email);
                    }}
                    className="hover:bg-white hover:bg-opacity-20 font-inter rounded-lg p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {/* Input field */}
              <input
                ref={inputRef}
                type="text"
                placeholder={
                  emailTags.length === 0
                    ? "Add email addresses (separate with comma, space, or Enter)"
                    : ""
                }
                value={emailInput}
                onChange={handleEmailInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onPaste={handlePaste}
                className="flex-1 min-w-[120px] py-1 self-stretch font-inter outline-none bg-transparent text-sm  3xl:text-base  font-normal placeholder-gray-500"
                aria-label="Add email addresses"
              />
            </div>
            {/* {emailTags.length > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                {emailTags.length} email{emailTags.length !== 1 ? "s" : ""}{" "}
                added
              </div>
            )} */}
          </section>

          {/* User List Section */}
          <section className="space-y-3 sm:space-y-4 xl:space-y-4 2xl:space-y-4 4xl:space-y-6">
            {/* Current User (Owner) */}
            {currentUser && (
              <div className="flex items-center justify-between gap-3 sm:gap-4 xl:gap-5 2xl:gap-6 4xl:gap-8">
                <div className="flex items-center gap-2 sm:gap-2 xl:gap-2 2xl:gap-3 4xl:gap-4 flex-1 min-w-0">
                  {/* User Profile Picture */}
                  <div className="w-6 h-6 sm:w-8 sm:h-8 xl:w-8 xl:h-8 2xl:w-8 2xl:h-8 4xl:w-12 4xl:h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img
                      src={profileImage}
                      alt={`${currentUser.name}'s profile`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* User Details */}
                  <div className="min-w-0 flex-1 space-y-0">
                    <div
                      className="text-base  3xl:text-lg  font-medium text-[#1E1F26]  truncate -mb-1"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      {currentUser.name}
                    </div>
                    <div
                      className="text-xs  3xl:text-sm  text-[#373843]  font-normal truncate"

                    >
                      {currentUser.email}
                    </div>
                  </div>
                </div>

                {/* Owner Badge */}
                <div className="flex flex-row items-start gap-1 sm:gap-2 4xl:gap-3 flex-shrink-0">
                  <span
                    className="text-xs sm:text-sm xl:text-[14px] 2xl:text-[14px] 3xl:text-base 4xl:text-lg text-[#8A8D9B]  font-normal"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Owner
                  </span>
                </div>
              </div>
            )}

            {/* Previous Developers (Viewers) */}
            {isLoadingDevelopers ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              previousDevelopers.map((developer) => (
                <div
                  key={developer.id}
                  className="flex items-center justify-between gap-3 sm:gap-4 xl:gap-5 2xl:gap-6 4xl:gap-8"
                >
                  <div className="flex items-center ggap-2 sm:gap-2 xl:gap-2 2xl:gap-3 4xl:gap-4 flex-1 min-w-0">
                    {/* Developer Avatar */}
                    <div className="w-6 h-6 sm:w-8 sm:h-8 xl:w-8 xl:h-8 2xl:w-8 2xl:h-8 4xl:w-12 4xl:h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {developer.avatar ? (
                        <img
                          src={developer.avatar}
                          alt={developer.name}
                          className="w-full h-full object-cover"
                          onLoad={() =>
                            console.log("✅ Image loaded:", developer.avatar)
                          }
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const fallback = document.createElement("div");
                              fallback.className =
                                "w-full h-full rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm";
                              fallback.textContent = getInitials(developer.name);
                              parent.appendChild(fallback);
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm">
                          {getInitials(developer.name)}
                        </div>
                      )}
                    </div>
                    {/* Developer Details */}
                    <div className="min-w-0 flex-1 space-y-0">
                      <div
                        className="text-base  3xl:text-lg  font-medium text-[#1E1F26]  truncate -mb-1"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        {developer.name}
                      </div>
                      <div
                        className="text-xs  3xl:text-sm   text-[#373843]  font-normal truncate"
                        
                      >
                        {developer.email}
                      </div>
                    </div>
                  </div>

                  {/* Viewer Badge */}
                  <div className="flex flex-row items-start gap-1 sm:gap-2 4xl:gap-3 flex-shrink-0">
                    <span
                     className="text-xs sm:text-sm xl:text-[14px] 2xl:text-[14px] 3xl:text-base 4xl:text-lg text-[#8A8D9B]  font-normal"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      Viewer
                    </span>
                  </div>
                </div>
              ))
            )}
          </section>
        </main>

        {/* Footer */}
        <footer className="flex items-center justify-between mx-4 sm:mx-4 xl:mx-6 2xl:mx-6 4xl:mx-12 py-3 sm:py-3 xl:py-3 2xl:py-3 4xl:py-6 border-t border-gray-200">
          <div
            className="text-base 3xl:text-lg  text-[#1E1F26]"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            Total feedback:{" "}
            <span className="text-[#1E1F26] ">{totalFeedback}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 xl:gap-3 2xl:gap-3 4xl:gap-4">
            <Button
              variant="cancel"
              size="lg"
              onClick={handleCancel}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              className="w-full sm:w-auto text-sm"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
               size="lg"
              onClick={handleShare}
              disabled={!isShareActive}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              className={`w-full sm:w-auto text-sm ${
                !isShareActive ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Share
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ShareFeedbackPopup;
